import React, { useState } from 'react';
import { motion } from 'motion/react';
import { Globe, Shield, RefreshCw, Layers, Database, Lock, Terminal, Radio, Server, Check, HelpCircle, HardDrive, Cpu, AlertTriangle } from 'lucide-react';
import { HostingDeployment, NexusUser } from '../types/nexus';

interface NexusHostingProps {
  user: NexusUser;
  onDeploy: () => void;
  onAddLog: (module: string, action: string, status: 'SUCCESS' | 'WARNING' | 'DENIED' | 'PENDING_APPROVAL', details: string) => void;
  isAdminMode: boolean;
}

export const NexusHosting: React.FC<NexusHostingProps> = ({
  user,
  onDeploy,
  onAddLog,
  isAdminMode
}) => {
  const [deployments, setDeployments] = useState<HostingDeployment[]>([
    {
      id: 'dep-01',
      domain: 'client-agency.nexorax.co.uk',
      sslStatus: 'Active',
      deploymentStatus: 'Ready',
      buildStatus: 'SUCCESS - compiled in 42.1s',
      storageUsage: '1.2 GB / 10 GB',
      bandwidthUsage: '12.4 GB / 100 GB',
      uptime: '99.98%'
    }
  ]);

  const [isDeploying, setIsDeploying] = useState(false);
  const [selectedDomain, setSelectedDomain] = useState('client-agency.nexorax.co.uk');
  const [newDomain, setNewDomain] = useState('');
  const [envVars, setEnvVars] = useState<{[key: string]: string}>({
    DATABASE_URL: 'postgresql://db_user:****************@0.0.0.0:5432/main_prod',
    GEMINI_API_KEY: 'AIzaSy************************************',
    METRICS_SECRET_HASH: 'd48cf525********************************'
  });
  const [showSecrets, setShowSecrets] = useState(false);

  // Admin Logs State
  const [serverLogs, setServerLogs] = useState<string[]>([
    "[SYSTEM] REVERSE PROXY ROUTING INITIALIZED ON PORT 3000",
    "[NGINX] upstream nexorax-applet-nodes connected successfully",
    "[SECURITY] SSL certified via Let's Encrypt CA root check passed",
    "[DOCKER] Container instance running with AMD EPYC 128 Core virtualization layer",
    "[BACKUP] Automated daily vault backup saved to CloudSync-02 repository on Google Drive",
    "[METRICS] Uptime stable. Peak ingress handled successfully at 0.04% CPU load."
  ]);

  const triggerDeploy = () => {
    setIsDeploying(true);
    onAddLog('Nexus Hosting', 'Trigger build deployment', 'SUCCESS', 'Began client site workspace build compile sequence.');

    setTimeout(() => {
      setIsDeploying(false);
      onDeploy();
      setDeployments(prev => prev.map(d => d.domain === selectedDomain ? {
        ...d,
        buildStatus: `SUCCESS - compiled in ${(Math.random() * 15 + 30).toFixed(1)}s`,
        deploymentStatus: 'Ready'
      } : d));
      onAddLog('Nexus Hosting', 'Build deploy succeeded', 'SUCCESS', 'Deployment build fully live on German Hetzner bare-metal Node controller.');
      alert('Deployment build loaded successfully!');
    }, 2000);
  };

  const handleAddDomain = (e: React.FormEvent) => {
    e.preventDefault();
    if (!newDomain) return;

    const newDep: HostingDeployment = {
      id: `dep-${Date.now()}`,
      domain: newDomain,
      sslStatus: 'Pending',
      deploymentStatus: 'Deploying',
      buildStatus: 'Initiating server-side provisioning',
      storageUsage: '0 GB / 10 GB',
      bandwidthUsage: '0 GB / 100 GB',
      uptime: '100%'
    };

    setDeployments([...deployments, newDep]);
    setSelectedDomain(newDomain);
    setNewDomain('');
    onAddLog('Nexus Hosting', `Connect custom domain: ${newDomain}`, 'SUCCESS', `Initiated SSL certificate generation for custom domain "${newDomain}".`);
  };

  return (
    <div className="w-full space-y-6">
      {/* Banner */}
      <div className="glass-panel border border-cyan-500/20 p-6 rounded-2xl bg-gradient-to-r from-purple-950/10 via-black/40 to-cyan-950/10">
        <h2 className="text-2xl font-sans font-black tracking-tight text-white uppercase flex items-center gap-2">
          <Globe className="w-6 h-6 text-cyan-400 animate-spin-slow" />
          Nexus Hosting
        </h2>
        <p className="text-xs font-mono text-cyan-450/80 uppercase tracking-widest mt-1">
          Managed Hosting for AI-Ready Websites and Workspaces
        </p>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Left column (Hostings and stats) */}
        <div className="lg:col-span-2 space-y-6">
          {/* Custom Domain Connector */}
          <div className="glass-panel border border-zinc-800 p-5 rounded-2xl bg-black/30 space-y-4">
            <h3 className="text-xs font-mono font-black text-white uppercase tracking-wider flex items-center gap-2">
              <RefreshCw className="w-3.5 h-3.5 text-cyan-400" />
              Connect Domain & Custom DNS Guide
            </h3>
            <form onSubmit={handleAddDomain} className="flex gap-2">
              <input
                type="text"
                placeholder="e.g. workspace.customdomain.com"
                value={newDomain}
                onChange={(e) => setNewDomain(e.target.value)}
                className="flex-grow bg-black/50 border border-zinc-800 rounded-lg px-3 py-2 text-xs font-mono text-white tracking-wider outline-none focus:border-cyan-400/50"
              />
              <button
                type="submit"
                className="px-4 py-2 bg-gradient-to-r from-cyan-500 to-purple-600 text-black font-mono text-[10px] font-black uppercase rounded-lg hover:opacity-95 active:scale-97 cursor-pointer"
              >
                Connect Domain
              </button>
            </form>

            <div className="bg-black/60 border border-zinc-900 rounded-lg p-3 text-[10px] font-mono text-slate-400 space-y-1">
              <div className="text-white font-extrabold pb-1">Required DNS Record Configuration Checklist:</div>
              <div>• A Record (points to bare-metal proxy): <strong className="text-cyan-400">188.40.168.109</strong> (Hetzner Dedicated Location)</div>
              <div>• CNAME (alternatives): <strong className="text-purple-400">edge.nexoraxdigital.co.uk</strong></div>
              <div className="font-sans text-[9px] text-slate-500 pt-1 leading-normal">
                Once records resolve globally, the Let's Encrypt automation auto-renews public SSL certificates within 2 minutes.
              </div>
            </div>
          </div>

          {/* Active Deployments */}
          <div className="space-y-4">
            <h3 className="text-xs font-mono font-black text-slate-400 uppercase tracking-widest pl-1">
              Active Hosting Deployments
            </h3>

            {deployments.map((dep) => (
              <div
                key={dep.id}
                onClick={() => setSelectedDomain(dep.domain)}
                className={`glass-panel border p-5 rounded-2xl bg-black/40 cursor-pointer transition-all ${
                  selectedDomain === dep.domain
                    ? 'border-[#00ffff]/40 shadow-[0_0_15px_rgba(0,255,255,0.06)]'
                    : 'border-zinc-850 hover:border-zinc-800'
                }`}
              >
                <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-2 border-b border-zinc-900/60 pb-3 mb-4">
                  <div>
                    <h4 className="text-sm font-sans font-bold text-white flex items-center gap-2">
                      <Server className="w-3.5 h-3.5 text-cyan-400" />
                      {dep.domain}
                    </h4>
                    <span className="text-[9px] font-mono text-slate-500 font-extrabold uppercase">
                      BUILD SYSTEM RUNNING COMPILER
                    </span>
                  </div>

                  <div className="flex items-center gap-2 text-[9px] font-mono">
                    <span className="px-2 py-0.5 rounded bg-cyan-950/40 border border-cyan-500/20 text-[#00ffff] flex items-center gap-1">
                      <Shield size={10} /> SSL {dep.sslStatus}
                    </span>
                    <span className={`px-2 py-0.5 rounded ${
                      dep.deploymentStatus === 'Ready'
                        ? 'bg-emerald-950/40 border border-emerald-500/20 text-emerald-400'
                        : 'bg-amber-950/40 border border-amber-500/20 text-amber-500'
                    } flex items-center gap-1`}>
                      <Lock size={10} /> {dep.deploymentStatus}
                    </span>
                  </div>
                </div>

                {/* Metering Metrics / Bars */}
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4 text-[10px] font-mono">
                  {/* Storage */}
                  <div className="space-y-1.5">
                    <div className="flex justify-between text-slate-500">
                      <span>STORAGE BASELINE:</span>
                      <span className="text-white">{dep.storageUsage}</span>
                    </div>
                    <div className="w-full bg-zinc-950 rounded-full h-1">
                      <div className="bg-gradient-to-r from-[#00ffff] to-purple-600 h-full rounded-full" style={{ width: '12%' }} />
                    </div>
                  </div>

                  {/* Bandwidth */}
                  <div className="space-y-1.5">
                    <div className="flex justify-between text-slate-500">
                      <span>MONTHLY BANDWIDTH:</span>
                      <span className="text-white">{dep.bandwidthUsage}</span>
                    </div>
                    <div className="w-full bg-zinc-950 rounded-full h-1">
                      <div className="bg-gradient-to-r from-purple-600 to-[#00ffff] h-full rounded-full" style={{ width: '12.4%' }} />
                    </div>
                  </div>

                  {/* Uptime */}
                  <div className="space-y-1.5">
                    <div className="flex justify-between text-slate-500">
                      <span>UPTIME AVERAGE (30D):</span>
                      <span className="text-emerald-400">{dep.uptime}</span>
                    </div>
                    <div className="w-full bg-zinc-950 rounded-full h-1">
                      <div className="bg-[#00ffff] h-full rounded-full" style={{ width: '99.9%' }} />
                    </div>
                  </div>
                </div>

                <div className="mt-4 pt-3 border-t border-zinc-900/60 flex items-center justify-between">
                  <span className="text-[10px] font-mono text-slate-500 uppercase">
                    Build status: <strong className="text-slate-350">{dep.buildStatus}</strong>
                  </span>
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      triggerDeploy();
                    }}
                    disabled={isDeploying}
                    className="py-1 px-3 bg-[#0a202a] hover:bg-[#0f3040] text-[#00ffff] border border-cyan-500/20 rounded font-mono text-[9px] uppercase font-black cursor-pointer"
                  >
                    {isDeploying ? 'Re-compiling...' : 'Trigger Deployment'}
                  </button>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Right column view - Client vs Admin details */}
        <div className="space-y-6">
          {/* Uptime security scans */}
          <div className="glass-panel border border-zinc-800 p-5 rounded-2xl bg-black/30 text-xs font-mono space-y-4">
            <h3 className="text-xs font-sans font-black text-white uppercase tracking-widest border-b border-zinc-900 pb-2 flex items-center gap-2">
              <Radio className="w-3.5 h-3.5 text-cyan-400" />
              Infrastructure Systems Telemetry
            </h3>
            <div className="space-y-2.5 text-[10px]">
              <div className="flex justify-between">
                <span className="text-slate-500">SERVER CORE:</span>
                <span className="text-white">German Location (NexoraX Core PX93)</span>
              </div>
              <div className="flex justify-between">
                <span className="text-slate-500">NGINX LOAD BALANCER:</span>
                <span className="text-emerald-400">ONLINE (Port 3000 Active)</span>
              </div>
              <div className="flex justify-between">
                <span className="text-slate-500">MONITOR SECTOR PIN:</span>
                <span className="text-emerald-400">0.02ms latency to cloud storage</span>
              </div>
              <div className="flex justify-between">
                <span className="text-slate-500">AUTO-BACKUP METERS:</span>
                <span className="text-cyan-400 uppercase">CloudSync-02 Verified</span>
              </div>
              <div className="flex justify-between">
                <span className="text-slate-500">SECURITY RISK LEVEL:</span>
                <span className="text-emerald-400">0% ACTIVE COMPROMISES</span>
              </div>
            </div>
            <button
              onClick={() => {
                onAddLog('Nexus Hosting', 'Interactive Security Scan', 'SUCCESS', 'Executed vulnerability payload scan. 100% clean.');
                alert('Vulnerability scanner complete on Hetzner Container stack. Root verification matched (No gaps identified).');
              }}
              className="w-full mt-3 py-2 bg-cyan-950/50 hover:bg-cyan-500/10 text-cyan-400 border border-cyan-500/25 rounded-xl font-mono text-[9px] uppercase font-black cursor-pointer"
            >
              Run Security Check
            </button>
          </div>

          {/* Admin Controls Panel */}
          <div className="glass-panel border border-purple-500/15 p-5 rounded-2xl bg-black/30 space-y-4 text-xs font-mono">
            <h3 className="text-xs font-sans font-black text-rose-400 uppercase tracking-widest border-b border-zinc-900 pb-2 flex items-center gap-2">
              <Terminal className="w-3.5 h-3.5" />
              Build Settings & Logs
            </h3>

            {isAdminMode ? (
              <div className="space-y-4">
                <div className="space-y-1.5">
                  <div className="flex justify-between text-[9px] text-zinc-500">
                    <span>BUILD INSTRUCTIONS COMMAND:</span>
                    <span className="text-zinc-600">CMD</span>
                  </div>
                  <input
                    type="text"
                    defaultValue="vite build && esbuild server.ts --bundle"
                    className="w-full bg-black/60 border border-zinc-850 px-2 py-1.5 text-[9px] text-slate-350 rounded font-mono outline-none"
                    disabled
                  />
                </div>

                <div className="space-y-2">
                  <div className="flex justify-between text-[10px] text-zinc-500 border-b border-zinc-900 pb-1">
                    <span>ENVIRONMENT VARIABLES:</span>
                    <button
                      onClick={() => setShowSecrets(!showSecrets)}
                      className="text-cyan-400 text-[8px] uppercase hover:underline"
                    >
                      {showSecrets ? 'Hide Variables' : 'Decrypt View'}
                    </button>
                  </div>
                  <div className="space-y-1.5 text-[8.5px]">
                    {Object.entries(envVars).map(([key, val]) => (
                      <div key={key} className="flex justify-between gap-2">
                        <span className="text-slate-400 font-bold">{key}:</span>
                        <span className="text-white break-all text-right font-light">
                          {showSecrets ? (val as string).replace(/\*/g, '') : (val as string)}
                        </span>
                      </div>
                    ))}
                  </div>
                </div>

                {/* Server build log console */}
                <div className="space-y-1">
                  <div className="text-[9px] text-slate-500 font-bold">CONSOLE LOGGER CONSOLE STREAM:</div>
                  <div className="h-32 overflow-y-auto bg-black/70 border border-zinc-900 rounded p-2 text-[8px] font-mono text-zinc-400 space-y-1 custom-scrollbar">
                    {serverLogs.map((log, i) => (
                      <div key={i} className="leading-relaxed border-l-2 border-cyan-500/30 pl-1">
                        {log}
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            ) : (
              <div className="p-3 bg-zinc-950/80 border border-zinc-900 rounded-lg text-center font-sans normal-case text-slate-400 space-y-2 leading-relaxed text-[11px]">
                <AlertTriangle className="w-5 h-5 text-amber-500 mx-auto animate-bounce mb-1" />
                <p>Admin Control dashboard is restricted to authorized systems administrators.</p>
                <p className="text-[10px] font-mono">Unlock Administrative Mode in the Control Plane to view environment logs and live server outputs.</p>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};
